import {
  ArrowRight,
  Circle,
  Diamond,
  Eraser,
  FileText,
  Hand,
  Lock,
  MousePointer,
  Pencil,
  Square,
  StickyNote,
  Trash2,
  User,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';
import { useCanvasStore, type ToolKind } from '../canvas/store';

const ParallelogramIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M4 18 L8 6 H20 L16 18 Z" />
  </svg>
);

const TOOLS: Array<{ kind: ToolKind; label: string; icon: React.ComponentType<{ className?: string }> }> = [
  { kind: 'select', label: 'Select (V)', icon: MousePointer },
  { kind: 'pan', label: 'Pan (Space)', icon: Hand },
  { kind: 'rect', label: 'Rectangle', icon: Square },
  { kind: 'ellipse', label: 'Ellipse', icon: Circle },
  { kind: 'diamond', label: 'Decision', icon: Diamond },
  { kind: 'parallelogram', label: 'Input', icon: ParallelogramIcon },
  { kind: 'cylinder', label: 'Database', icon: Lock },
  { kind: 'document', label: 'Document', icon: FileText },
  { kind: 'person', label: 'Person', icon: User },
  { kind: 'note', label: 'Note', icon: StickyNote },
  { kind: 'text', label: 'Text', icon: Pencil },
  { kind: 'arrow', label: 'Arrow', icon: ArrowRight },
];

export const Toolbar = () => {
  const tool = useCanvasStore((s) => s.tool);
  const setTool = useCanvasStore((s) => s.setTool);
  const selectedIds = useCanvasStore((s) => s.selectedIds);
  const removeElements = useCanvasStore((s) => s.removeElements);
  const setElements = useCanvasStore((s) => s.setElements);
  const viewport = useCanvasStore((s) => s.viewport);
  const setViewport = useCanvasStore((s) => s.setViewport);

  const zoom = (factor: number) => {
    const center = { x: window.innerWidth / 2, y: window.innerHeight / 2 };
    const newScale = Math.max(0.2, Math.min(4, viewport.scale * factor));
    setViewport({
      scale: newScale,
      x: center.x - (center.x - viewport.x) * (newScale / viewport.scale),
      y: center.y - (center.y - viewport.y) * (newScale / viewport.scale),
    });
  };

  const clearCanvas = () => {
    if (typeof window !== 'undefined' && !window.confirm('Clear the entire canvas? (saved project file will be empty)')) {
      return;
    }
    setElements([]);
  };

  return (
    <div className="pointer-events-auto flex flex-col gap-1 rounded-xl border border-slate-800 bg-slate-900/90 p-2 shadow-soft backdrop-blur">
      {TOOLS.map(({ kind, label, icon: Icon }) => (
        <button
          key={kind}
          className="tool-btn"
          title={label}
          data-active={tool === kind}
          onClick={() => setTool(kind)}
        >
          <Icon className="h-4 w-4" />
        </button>
      ))}
      <div className="my-1 h-px bg-slate-800" />
      <button
        className="tool-btn"
        title="Delete selected (Del)"
        disabled={!selectedIds.length}
        onClick={() => removeElements(selectedIds)}
      >
        <Trash2 className="h-4 w-4" />
      </button>
      <button
        className="tool-btn text-amber-300"
        title="Clear entire canvas"
        onClick={clearCanvas}
      >
        <Eraser className="h-4 w-4" />
      </button>
      <div className="my-1 h-px bg-slate-800" />
      <button className="tool-btn" title="Zoom in" onClick={() => zoom(1.2)}>
        <ZoomIn className="h-4 w-4" />
      </button>
      <button className="tool-btn" title="Zoom out" onClick={() => zoom(1 / 1.2)}>
        <ZoomOut className="h-4 w-4" />
      </button>
      <div className="mt-1 px-1 text-center text-[10px] text-slate-500">
        {Math.round(viewport.scale * 100)}%
      </div>
    </div>
  );
};
